import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  CardGroup,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
} from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import PhoneInput from 'react-phone-number-input';
import { get } from 'lodash-es';
import { CustomButton, CustomDashboardInput, Spinner } from '../../../containers';
import { redirectToAdminUserListPage } from '../../../redux/actions/navigationActions';
import {
  USER_ACCOUNT_TYPES,
  USER_ACCOUNT_BUSINESS,
  USER_GENDER,
  USER_ACCOUNT_PRIVATE,
} from '../../../constants/userDefs';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { validateFormSubmission } from '../../../utils/validation';
import { createUser } from '../../../redux/actions/usersActions';
import { useQuery } from 'react-apollo';
import { GetCountriesQuery } from '../../../operations/queries/GetLocationsQuery';
import { UserCreatePrivateForm } from './UserCreatePrivateForm';
import { UserCreateBusinessForm } from './UserCreateBusinessForm';
import { usePlacesQuery } from '../../../utils/customHooks';

export const AdminCreateUserPage = () => {
  const dispatch = useDispatch();
  const { places, loading: loadingPlaces } = usePlacesQuery();
  const { data: dataCountries, loading: loadingCountries } = useQuery(GetCountriesQuery);
  const countries = get(dataCountries, 'countries.countries', []).sort((a, b) =>
    a.nameCro > b.nameCro ? 1 : b.nameCro > a.nameCro ? -1 : 0
  );

  const creatingUserInProgress = useSelector(state => state.users.creatingUserInProgress);
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [email, setEmail] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.email" />,
    validation: {
      required: true,
      email: true,
    },
  });
  const [tempPassword, setTempPassword] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.temporaryPassword" />,
    validation: {
      required: true,
    },
  });
  const [phoneNumber, setPhoneNumber] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.phoneNumber" />,
    validation: {
      required: true,
    },
  });
  const [firstName, setFirstName] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.firstName" />,
    validation: {
      required: true,
    },
  });
  const [lastName, setLastName] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.lastName" />,
    validation: {
      required: true,
    },
  });
  const [sector, setSector] = useState({
    value: USER_ACCOUNT_PRIVATE,
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.sector" />,
    validation: {
      required: true,
      enum: USER_ACCOUNT_TYPES.map(type => type.name),
    },
  });
  const [OIB, setOIB] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.OIB" />,
    validation: {
      required: true,
      maxLength: 11,
      minLength: 11,
      oib: true,
    },
  });
  const [dateOfBirth, setDateOfBirth] = useState({
    value: null,
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.dateOfBirth" />,
    validation: {
      required: true,
      date: {
        isValid: true,
      },
    },
  });
  const [sex, setSex] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.sex" />,
    validation: {
      required: true,
      enum: USER_GENDER.map(gender => gender.value),
    },
  });
  const [address, setAddress] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.address" />,
    validation: {
      required: true,
    },
  });
  const [zip, setZip] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.zipCode" />,
    validation: {
      required: true,
      enum: places.map(place => place.pttNumber),
    },
  });
  const [city, setCity] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.place" />,
    validation: {
      required: true,
      enum: places.map(place => place.name),
    },
  });
  const [passportNumber, setPassportNumber] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.passportNumber" />,
    validation: {},
  });
  const [governmentIssuedIdNumber, setGovernmentIssuedIdNumber] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.governmentIssuedIdNumber" />,
    validation: {},
  });
  const [HZZONumber, setHZZONumber] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.HZZONumber" />,
    validation: {
      minLength: 9,
    },
  });
  const [ino, setIno] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.ino" />,
    validation: {},
  });

  const [businessName, setBusinessName] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.name" />,
    validation: {
      required: true,
    },
  });

  const [mbo, setMbo] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.MBO" />,
    validation: {},
  });
  const [VATpayer, setVATpayer] = useState({
    value: false,
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.VATpayer" />,
    validation: {
      required: true,
    },
  });
  const [country, setCountry] = useState({
    value: 'HR',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.country" />,
    validation: {
      required: true,
      enum: countries.map(country => country.codeLegacy),
    },
  });
  const [businessFirstName, setBusinessFirstName] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.firstName" />,
    validation: {},
  });
  const [businessLastName, setBusinessLastName] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.lastName" />,
    validation: {},
  });
  const [businessTelephone, setBusinessTelephone] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.businessTelephone" />,
    validation: {},
  });
  const [webAddress, setWebAddress] = useState({
    value: '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.webAddress" />,
    validation: {},
  });

  useEffect(() => {
    if (places.length > 0 && city.validation.enum.length < 1) {
      setCity(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.name) } }));
    }
    if (places.length > 0 && zip.validation.enum.length < 1) {
      setZip(o => ({ ...o, validation: { ...o.validation, enum: places.map(place => place.pttNumber) } }));
    }

    if (countries.length > 0 && country.validation.enum.length < 1) {
      setCountry(o => ({ ...o, validation: { ...o.validation, enum: countries.map(country => country.codeLegacy) } }));
    }
  }, [places, countries, city, country, setCity, setCountry, zip, setZip]);

  const toggleModal = () => {
    setIsOpen(isOpen => !isOpen);
  };

  const inputHandler = (setter, value) => {
    setter(o => ({ ...o, value, invalid: false, error: '' }));
  };

  const handleCreatePrivateUserForm = async () => {
    const fieldsArray = [
      setEmail,
      setTempPassword,
      setPhoneNumber,
      setFirstName,
      setLastName,
      setSector,
      setOIB,
      setDateOfBirth,
      setSex,
      setAddress,
      setZip,
      setCity,
      setCountry,
      setPassportNumber,
      setGovernmentIssuedIdNumber,
      setHZZONumber,
      setIno,
    ];

    setSubmitted(true);
    if (await validateFormSubmission(fieldsArray)) {
      dispatch(
        createUser({
          email: email.value,
          password: tempPassword.value,
          phoneNumber: phoneNumber.value,
          firstName: firstName.value,
          lastName: lastName.value,
          sector: sector.value,
          oib: OIB.value,
          dateOfBirth: dateOfBirth.value,
          sex: sex.value,
          address: address.value,
          zip: zip.value,
          city: city.value,
          country: country.value,
          passportNumber: passportNumber.value,
          governmentIssuedIdNumber: governmentIssuedIdNumber.value,
          HZZONumber: HZZONumber.value,
          ino: ino.value,
        })
      );
    }
  };

  const handleCreateBusinessUserForm = async () => {
    const fieldsArray = [
      setEmail,
      setTempPassword,
      setPhoneNumber,
      setSector,
      setOIB,
      setMbo,
      setBusinessName,
      setVATpayer,
      setAddress,
      setCity,
      setZip,
      setCountry,
      setBusinessFirstName,
      setBusinessLastName,
      setBusinessTelephone,
      setWebAddress,
    ];

    setSubmitted(true);
    if (await validateFormSubmission(fieldsArray)) {
      dispatch(
        createUser({
          email: email.value,
          password: tempPassword.value,
          phoneNumber: phoneNumber.value,
          sector: sector.value,
          oib: OIB.value,
          mbo: mbo.value,
          businessName: businessName.value,
          VATpayer: VATpayer.value,
          address: address.value,
          zip: zip.value,
          city: city.value,
          country: country.value,
          businessFirstName: businessFirstName.value,
          businessLastName: businessLastName.value,
          businessTelephone: businessTelephone.value,
          webAddress: webAddress.value,
        })
      );
    }
  };

  const handleCreateUserForm = event => {
    event.preventDefault();
    if (sector.value === USER_ACCOUNT_BUSINESS) {
      handleCreateBusinessUserForm();
    } else {
      handleCreatePrivateUserForm();
    }
  };

  return loadingPlaces || loadingCountries ? (
    <Spinner className="spinner-positioning" />
  ) : (
    <Container fluid>
      <Row>
        <Col xs="12" xl={{ size: 8, offset: 2 }}>
          <Row className="mb-3">
            <Col xs="12" className="">
              <div className="d-flex align-items-center">
                <h3 className="mb-0">
                  <FormattedMessage id="adminCreateUserPage.title" />
                </h3>
              </div>
            </Col>
          </Row>

          <Form onSubmit={handleCreateUserForm}>
            <Row>
              <Col>
                <CardGroup>
                  <Card>
                    <CardBody className="py-5">
                      <Row>
                        <Col>
                          <h3>
                            <FormattedMessage id="adminCreateUserPage.userData" /> *
                          </h3>
                        </Col>
                      </Row>
                      <FormGroup row className="mt-3">
                        <Col xs="12" sm="6">
                          <Label for="email">
                            <FormattedMessage id="inputs.email" />
                          </Label>
                          <InputGroup>
                            <CustomDashboardInput
                              id="email"
                              fieldState={email}
                              onChange={event => inputHandler(setEmail, event.target.value)}
                            />
                          </InputGroup>
                        </Col>
                        <Col xs="12" sm="6">
                          <Label for="temporaryPassword">
                            <FormattedMessage id="inputs.temporaryPassword" />
                          </Label>
                          <InputGroup>
                            <CustomDashboardInput
                              id="temporaryPassword"
                              name="temporary password"
                              type="password"
                              fieldState={tempPassword}
                              onChange={event => inputHandler(setTempPassword, event.target.value)}
                            />
                          </InputGroup>
                        </Col>
                      </FormGroup>
                      <FormGroup row className="mb-5">
                        <Col xs="12" sm="6">
                          <Label for="phoneNumber">
                            <FormattedMessage id="inputs.phoneNumber" />
                          </Label>
                          <InputGroup>
                            <CustomDashboardInput
                              id="phoneNumber"
                              name="mobile phone number"
                              type="tel"
                              // If lenght is greater than 21 formatPhoneNumberIntl() function from
                              // "react-phone-number-input" goes wild and resets the value to an empty string
                              maxLength={21}
                              tag={PhoneInput}
                              addInternationalOption={false}
                              defaultCountry="HR"
                              fieldState={phoneNumber}
                              numberInputProps={phoneNumber.invalid && submitted ? 'is-invalid' : ''}
                              onChange={phone =>
                                setPhoneNumber({ ...phoneNumber, invalid: false, value: formatPhoneNumber(phone) })
                              }
                            />
                          </InputGroup>
                        </Col>
                      </FormGroup>
                      {sector.value === USER_ACCOUNT_BUSINESS ? (
                        <UserCreateBusinessForm
                          inputHandler={inputHandler}
                          places={places}
                          loadingPlaces={loadingPlaces}
                          countries={countries}
                          loadingCountries={loadingCountries}
                          sector={sector}
                          setSector={setSector}
                          submitted={submitted}
                          OIB={OIB}
                          setOIB={setOIB}
                          mbo={mbo}
                          setMbo={setMbo}
                          businessName={businessName}
                          setBusinessName={setBusinessName}
                          VATpayer={VATpayer}
                          setVATpayer={setVATpayer}
                          address={address}
                          setAddress={setAddress}
                          city={city}
                          setCity={setCity}
                          zip={zip}
                          setZip={setZip}
                          country={country}
                          setCountry={setCountry}
                          businessFirstName={businessFirstName}
                          setBusinessFirstName={setBusinessFirstName}
                          businessLastName={businessLastName}
                          setBusinessLastName={setBusinessLastName}
                          businessTelephone={businessTelephone}
                          setBusinessTelephone={setBusinessTelephone}
                          webAddress={webAddress}
                          setWebAddress={setWebAddress}
                        />
                      ) : (
                        <UserCreatePrivateForm
                          inputHandler={inputHandler}
                          places={places}
                          loadingPlaces={loadingPlaces}
                          countries={countries}
                          loadingCountries={loadingCountries}
                          sector={sector}
                          setSector={setSector}
                          submitted={submitted}
                          OIB={OIB}
                          setOIB={setOIB}
                          firstName={firstName}
                          setFirstName={setFirstName}
                          lastName={lastName}
                          setLastName={setLastName}
                          dateOfBirth={dateOfBirth}
                          setDateOfBirth={setDateOfBirth}
                          sex={sex}
                          setSex={setSex}
                          address={address}
                          setAddress={setAddress}
                          city={city}
                          setCity={setCity}
                          zip={zip}
                          setZip={setZip}
                          country={country}
                          setCountry={setCountry}
                          passportNumber={passportNumber}
                          setPassportNumber={setPassportNumber}
                          governmentIssuedIdNumber={governmentIssuedIdNumber}
                          setGovernmentIssuedIdNumber={setGovernmentIssuedIdNumber}
                          HZZONumber={HZZONumber}
                          setHZZONumber={setHZZONumber}
                          ino={ino}
                          setIno={setIno}
                        />
                      )}
                    </CardBody>
                  </Card>
                </CardGroup>
              </Col>
            </Row>
            <Row className="no-gutters mt-5">
              <Col className="text-right" xs="12">
                <CustomButton
                  className="hok-outline-btn d-block d-sm-inline-block mb-3 mb-sm-0 mr-0 mr-sm-3 ml-auto ml-sm-0"
                  outline
                  block={false}
                  translationId="cancel"
                  onClick={event => {
                    event.preventDefault();
                    setIsOpen(true);
                  }}
                  disabled={creatingUserInProgress}
                />
                <CustomButton
                  type="submit"
                  className="hok-dashboard-btn d-block d-sm-inline-block ml-auto"
                  block={false}
                  translationId="create"
                  loaderProp={creatingUserInProgress}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Modal modalClassName="hok-modal" centered backdrop="static" toggle={toggleModal} isOpen={isOpen}>
        <ModalHeader className="border-0">
          <Row className="no-gutters auth-nav-links">
            <Col xs={12}>
              <h2 className="d-flex align-items-center justify-content-center">
                <FormattedMessage id="adminCreateUserPage.cancelModalTitle" />
              </h2>
            </Col>
          </Row>
        </ModalHeader>
        <ModalBody>
          <Row className="no-gutters px-4 font-weight-500">
            <FormattedMessage id="adminCreateUserPage.areYouSureToCancelUserCreation" />
          </Row>
        </ModalBody>
        <ModalFooter className="justify-content-center">
          <Row className="w-100">
            <Col xs={6}>
              <CustomButton
                className="hok-outline-btn"
                outline
                translationId="close"
                onClick={() => setIsOpen(false)}
              />
            </Col>
            <Col xs={6}>
              <CustomButton
                onClick={() => dispatch(redirectToAdminUserListPage())}
                type="submit"
                className="hok-dashboard-btn"
                translationId="confirm"
              />
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </Container>
  );
};
